/* oxlint-disable no-console -- recovery path; no TelemetryPort here */
import type { Database } from 'sql.js';

/**
 * Copy offline work rows from a recovery-backup sql.js DB into a freshly seeded target.
 * Uses FK OFF so missing catalog refs do not block restore; next catalog pull repairs refs.
 */

const WORK_TABLES_IN_ORDER = [
  'beneficiarios',
  'beneficiario_item',
  'entregas_paquetes',
  'entrega_nutricionista',
  'entrega_participantes',
  'entrega_participante_producto',
  'entrega_remisiones',
  'reposiciones_productos',
  'reposicion_producto_item',
] as const;

export type RecoveryMergeResult = {
  ok: boolean;
  tablesCopied: number;
  rowsCopied: number;
  error?: string;
};

function tableExists(db: Database, table: string): boolean {
  const stmt = db.prepare(
    `SELECT 1 AS ok FROM sqlite_master WHERE type = 'table' AND name = ? LIMIT 1`,
  );
  try {
    stmt.bind([table]);
    return stmt.step();
  } finally {
    stmt.free();
  }
}

function columnNames(db: Database, table: string): string[] {
  const cols: string[] = [];
  const stmt = db.prepare(`PRAGMA table_info(${table})`);
  try {
    while (stmt.step()) {
      const row = stmt.getAsObject() as { name?: string };
      if (typeof row.name === 'string' && row.name !== 'id') cols.push(row.name);
    }
  } finally {
    stmt.free();
  }
  return cols;
}

function copyTable(source: Database, target: Database, table: string): number {
  if (!tableExists(source, table) || !tableExists(target, table)) return 0;
  const cols = columnNames(source, table).filter((c) => columnNames(target, table).includes(c));
  if (cols.length === 0) return 0;
  const colList = cols.join(', ');
  const placeholders = cols.map(() => '?').join(', ');
  const insertSql = `INSERT OR IGNORE INTO ${table} (${colList}) VALUES (${placeholders})`;
  const select = source.prepare(`SELECT ${colList} FROM ${table}`);
  let n = 0;
  try {
    while (select.step()) {
      const row = select.getAsObject() as Record<string, unknown>;
      const values = cols.map((c) => row[c] ?? null);
      target.run(insertSql, values);
      n += 1;
    }
  } finally {
    select.free();
  }
  return n;
}

function mergeSyncMeta(source: Database, target: Database): void {
  if (!tableExists(source, 'sync_meta') || !tableExists(target, 'sync_meta')) return;
  const stmt = source.prepare(
    `SELECT last_pull_at, last_error FROM sync_meta WHERE id = 1 LIMIT 1`,
  );
  try {
    if (!stmt.step()) return;
    const row = stmt.getAsObject() as { last_pull_at?: string | null; last_error?: string | null };
    target.run(`UPDATE sync_meta SET last_pull_at = ?, last_error = ? WHERE id = 1`, [
      row.last_pull_at ?? null,
      row.last_error ?? null,
    ]);
  } finally {
    stmt.free();
  }
}

/** Merge pending offline work from an open backup Database into `target`. Closes nothing. */
export function mergePendingWorkFromBackupDb(target: Database, backup: Database): RecoveryMergeResult {
  let tablesCopied = 0;
  let rowsCopied = 0;
  try {
    target.run('PRAGMA foreign_keys = OFF');
    backup.run('PRAGMA foreign_keys = OFF');
    for (const table of WORK_TABLES_IN_ORDER) {
      const n = copyTable(backup, target, table);
      if (n > 0) {
        tablesCopied += 1;
        rowsCopied += n;
      }
    }
    mergeSyncMeta(backup, target);
    target.run('PRAGMA foreign_keys = ON');
    return { ok: true, tablesCopied, rowsCopied };
  } catch (e) {
    const error = e instanceof Error ? e.message : String(e);
    console.error('[local-sqlite] recovery merge failed', e);
    try {
      target.run('PRAGMA foreign_keys = ON');
    } catch {
      /* ignore */
    }
    return { ok: false, tablesCopied, rowsCopied, error };
  }
}
